import { AllowedUserFieldsEnum } from 'shared/accountNameConfig'
import * as yup from 'yup'
import validations from '../validations'

export enum UserExtraValidationSchemaEnum {
  PASSWORD = 'password',
  PASSWORD_CONFIRMATION = 'passwordConfirmation',
  PRIVACY_POLICY = 'privacyPolicy'
}

export type UserValidationFields =
  | UserExtraValidationSchemaEnum
  | AllowedUserFieldsEnum

export type UserFieldsConfigMapOptionsType<
  T extends ValidationErrorsMessagesType
> = {
  privacyRequired?: boolean
  requireAdditionalFields: boolean
  errorMessages: T
}
type ValidationErrorsMessagesType = {
  passwordValidationMessage?: string
  defaultNonNullMessage?: string
  privacyPolicyValidationMessage?: string
  bothPasswordNeedToBeTheSameMessage?: string
  emailRequiredMessage?: string
}

const getUserFieldsConfigMap = <T extends ValidationErrorsMessagesType>({
  errorMessages,
  requireAdditionalFields
}: UserFieldsConfigMapOptionsType<T>): Record<
  UserValidationFields,
  yup.AnySchema
> => {
  const {
    defaultNonNullMessage,
    passwordValidationMessage,
    privacyPolicyValidationMessage,
    bothPasswordNeedToBeTheSameMessage,
    emailRequiredMessage
  } = errorMessages
  const requiredStringSchema = yup.string().required(defaultNonNullMessage)
  const optionalStringSchema = yup.string().optional()
  const additionalFieldsValidationsSchema = requireAdditionalFields
    ? requiredStringSchema
    : optionalStringSchema
  const schema = {
    [UserExtraValidationSchemaEnum.PASSWORD]: yup
      .string()
      .min(8, passwordValidationMessage)
      .matches(validations.passwordRegex, passwordValidationMessage)
      .required(defaultNonNullMessage),
    [UserExtraValidationSchemaEnum.PASSWORD_CONFIRMATION]: yup
      .string()
      .when(UserExtraValidationSchemaEnum.PASSWORD, {
        is: (psw: string) => (psw && psw.length > 0 ? true : false),
        then: yup
          .string()
          .oneOf(
            [yup.ref(UserExtraValidationSchemaEnum.PASSWORD)],
            bothPasswordNeedToBeTheSameMessage
          )
      })
      .required(defaultNonNullMessage),
    [AllowedUserFieldsEnum.EMAIL]: yup
      .string()
      .email()
      .required(emailRequiredMessage),

    [AllowedUserFieldsEnum.ACCOUNT_NAME]: requiredStringSchema.matches(
      validations.accountName
    ),
    [AllowedUserFieldsEnum.FIRST_NAME]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.LAST_NAME]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.DISPLAY_NAME]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.PHONE_NUMBER]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.POSITION]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.AREA]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.LOCATION]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.DEPARTMENT]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.CUSTOM_1]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.CUSTOM_2]: additionalFieldsValidationsSchema,
    [AllowedUserFieldsEnum.CUSTOM_3]: additionalFieldsValidationsSchema,
    [UserExtraValidationSchemaEnum.PRIVACY_POLICY]: yup
      .bool()
      .oneOf([true], privacyPolicyValidationMessage)
      .required(privacyPolicyValidationMessage)
  }

  return schema
}

const buildValidationSchemaFromFields = <K extends string, T extends K>(
  allFieldsSchema: Record<K, yup.AnySchema>,
  fields: T[]
): Record<T, yup.AnySchema> =>
  Object.fromEntries(
    [...new Set(fields)].map((field) => [field, allFieldsSchema[field]])
  ) as Record<T, yup.AnySchema>

export const getValidationSchemaForUserFields = <
  T extends ValidationErrorsMessagesType
>({
  requiredFields,
  ...opts
}: UserFieldsConfigMapOptionsType<T> & {
  requiredFields: UserValidationFields[]
}) => {
  return buildValidationSchemaFromFields(
    getUserFieldsConfigMap(opts),
    requiredFields
  )
}

export const extendsRequiredFieldsWithUserIdentifier = (
  requiredFields: UserValidationFields[],
  isAccountNameLogin: boolean
): UserValidationFields[] => {
  return [
    ...new Set([
      ...requiredFields,
      isAccountNameLogin
        ? AllowedUserFieldsEnum.ACCOUNT_NAME
        : AllowedUserFieldsEnum.EMAIL
    ])
  ]
}
